import {useCallback, useRef} from 'react';

import makeCancelable from 'lib/makeCancelable';
import notifications, {GenericResponseJSON} from 'notifications';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import * as net from 'util/Net';
import {PromiseFn} from 'util/Types';

export enum AsyncStatus {
  NOT_FETCHED = 'NOT_FETCHED',
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export interface NotFetchedAsyncState {
  status: AsyncStatus.NOT_FETCHED;
}

export interface LoadingAsyncState<TData, TInput = undefined> {
  status: AsyncStatus.LOADING;
  previousData: TData | null;
  input?: TInput;
  cancel?: () => void;
}

export interface SuccessAsyncState<TData, TInput = undefined> {
  status: AsyncStatus.SUCCESS;
  data: TData;
  input?: TInput;
}

export interface ErrorAsyncState<TInput = undefined> {
  status: AsyncStatus.ERROR;
  message: string;
  responseJSON: GenericResponseJSON;
  input?: TInput;
}

export type AsyncState<TData, TInput = undefined> =
  | NotFetchedAsyncState
  | LoadingAsyncState<TData, TInput>
  | SuccessAsyncState<TData, TInput>
  | ErrorAsyncState<TInput>;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type AsyncCallParams = Array<any>;

export const NOT_FETCHED_ASYNC_STATE: NotFetchedAsyncState = {status: AsyncStatus.NOT_FETCHED};

export interface MakeAsyncCallInput<TParams extends AsyncCallParams, TReturn> {
  makeCall: PromiseFn<TParams, TReturn>;
  args: TParams;
  asyncState: AsyncState<TReturn, TParams>;
  setAsyncState: (newAsyncState: AsyncState<TReturn, TParams>) => void;
  includeInput?: boolean;
  keepOnlyLatest?: boolean;
}

/**
 * Track state of an async call in a controlled way
 *
 * Controlled meaning its the asyncState and its setter as inputs to the function. useAsyncState uses this
 * with the useState hook to track state at the component level. However, callers can instead use this
 * function to track state elsewhere (for example, GraphQL or Redux).
 */
export async function makeAsyncCall<TParams extends AsyncCallParams, TReturn>({
  makeCall,
  args,
  asyncState,
  setAsyncState,
  includeInput = false,
  keepOnlyLatest = false,
}: MakeAsyncCallInput<TParams, TReturn>): Promise<void> {
  try {
    if (asyncState.status === AsyncStatus.LOADING && keepOnlyLatest && asyncState.cancel != null) {
      asyncState.cancel();
    }
    const {promise, cancel} = makeCancelable(makeCall(...args));
    setAsyncState({
      status: AsyncStatus.LOADING,
      previousData: getMostRecentData(asyncState),
      ...(includeInput ? {input: args} : {}),
      ...(keepOnlyLatest ? {cancel} : {}),
    });
    const data = await promise;
    setAsyncState({
      status: AsyncStatus.SUCCESS,
      data,
      ...(includeInput ? {input: args} : {}),
    });
  } catch (err) {
    if (!err.isCanceled) {
      if (net.isServerRequestError(err)) {
        setAsyncState({
          status: AsyncStatus.ERROR,
          message: notifications.getFriendlyError(err) || GENERIC_ERROR_MESSAGE,
          responseJSON: err,
          ...(includeInput ? {input: args} : {}),
        });
      } else {
        throw err;
      }
    }
  }
}

interface UseAsyncStateArgs<TParams extends AsyncCallParams, TReturn> {
  /** Async function that makes the async call */
  makeCall: PromiseFn<TParams, TReturn>;
  /**
   * Determines whether to return the args to makeCall on currentState
   *
   * @default false
   */
  includeInput?: boolean;
  /**
   * If true, only keep the results of the latest `makeCall` in state between calls.
   *
   * @default false
   */
  keepOnlyLatest?: boolean;
}

/**
 * React hook to track state of an async call
 *
 * Useful to store the state of an async call (aka server call, DAO call) within the component itself in a
 * lightweight way, without needing Redux.
 *
 * See AsyncState for the possible states:
 *   - Status can be LOADING, SUCCESS, ERROR, etc.
 *   - Additionally track the successful call's data, error message, etc.
 *
 * See useAsyncState.stories.tsx for a demo.
 *
 * @param {Type} T - shape of the data (the successful call's return value)
 * @param {Function} makeCall - async function that makes the async call
 * @param {boolean} [includeInput=false] - Determines whether to return the args to makeCall on currentState
 * @param {boolean} [keepOnlyLatest=false] - If true, only keep the results of the latest `makeCall` in state between calls.
 * @return {Function} currentState - state of the async call
 * @return {Function} makeCall - the wrapped async function. Wrapped so useAsyncState can track the state of the call.
 */
export default function useAsyncState<TParams extends AsyncCallParams, TReturn>({
  makeCall,
  includeInput = false,
  keepOnlyLatest = false,
}: UseAsyncStateArgs<TParams, TReturn>): {
  currentState: AsyncState<TReturn, TParams>;
  makeCall: (...args: TParams) => Promise<void>;
} {
  const [asyncState, setAsyncState] =
    useStateIfMounted<AsyncState<TReturn, TParams>>(NOT_FETCHED_ASYNC_STATE);
  const latestStateRef = useRef(asyncState);
  latestStateRef.current = asyncState;
  const wrappedMakeCall = useCallback(
    (...args: TParams) =>
      makeAsyncCall<TParams, TReturn>({
        makeCall,
        args,
        asyncState: latestStateRef.current,
        setAsyncState,
        includeInput,
        keepOnlyLatest,
      }),
    [includeInput, makeCall, keepOnlyLatest, setAsyncState]
  );
  return {
    currentState: asyncState,
    makeCall: wrappedMakeCall,
  };
}

/**
 * Return data from the most recent call (if it was successful). Useful to show the (stale) data from the
 * previous call while the next call is loading.
 */
export function getMostRecentData<TData, TInput>(currentState: AsyncState<TData, TInput>): TData | null {
  switch (currentState.status) {
    case AsyncStatus.LOADING:
      return currentState.previousData;
    case AsyncStatus.SUCCESS:
      return currentState.data;
    default:
      return null;
  }
}

export function useAsyncStateOnMount<TReturn>(makeCall: PromiseFn<[], TReturn>): AsyncState<TReturn, []> {
  const {currentState, makeCall: wrappedMakeCall} = useAsyncState<[], TReturn>({makeCall});

  useOnMount(() => {
    wrappedMakeCall();
  });

  return currentState;
}
